/**
 * Strategic Partners — shared data source for /ops and /partners pages.
 *
 * Anyone consuming this must treat it as read-only. When migrating to backend,
 * replace the static exports with API-loaded state via a hook (e.g. usePartners()).
 */

// Strategic partner grouping — Industrial Innovation Platform taxonomy.
export const STRATEGIC_GROUPS = [
  { id: "industrial-digitalisation", label: "Industrial Digitalisation",   accent: "#3B82F6" },
  { id: "precision-vision",          label: "Precision Vision & Metrology", accent: "#A855F7" },
  { id: "renewable-infrastructure",  label: "Renewable Infrastructure",     accent: "#22C55E" },
  { id: "autonomous-manufacturing",  label: "Autonomous Manufacturing",     accent: "#F59E0B" },
  { id: "applied-research",          label: "Applied Research",             accent: "#EAB308" },
  { id: "health-intelligence",       label: "Health Intelligence",          accent: "#14B8A6" },
];

export const SUPPLIERS = [
  {
    id: "siemens",
    name: "Siemens",
    logo: null,
    group: "industrial-digitalisation",
    status: "Proposed Collaboration",
    category: "Digital Twin · Automation · Industrial Software",
    country: "Germany",
    description: "Global industrial digitalisation leader — Digital Twin platforms, industrial automation, industrial software (Xcelerator) and Smart Infrastructure. Backbone of the NeuNexus Industry 4.0 blueprint.",
    contact: { name: "Strategic Partnerships", email: "", phone: "", website: "https://www.siemens.com" },
    documents: [],
    tags: ["Digital Twin", "Industrial Automation", "Industrial Software", "Smart Infrastructure", "Xcelerator"],
  },
  {
    id: "vcow",
    name: "VCOW",
    logo: null,
    product_image: "/vcow/product-ipc-boxes.jpg",
    group: "industrial-digitalisation",
    status: "Proposed Collaboration",
    category: "Industrial Edge · AI Compute",
    country: "Germany",
    description: "Industrial edge computing, ruggedised industrial PCs and AI compute infrastructure for factory-floor workloads. Enables real-time inference and Digital Twin execution close to the process. Reference product line: IPC-BOX-D6-M-0P fanless industrial edge computer (multi-LAN, USB 3.2, HDMI, DC 9-36 V) — CE-certified (EN 55032:2015+A11:2020+A1:2020, EN IEC 61000-3-2:2019+A1:2021+A2:2024, EN 61000-3-3:2013+A1:2019+A2:2021, EN 55035:2017+A11:2020) under EMC Directive 2014/30/EU.",
    contact: { name: "Business Development", email: "", phone: "", website: "" },
    documents: [
      { title: "IPC-BOX-D6-M-0P · Datasheet", type: "PDF" },
      { title: "CE Certificate of Conformity",  type: "PDF" },
    ],
    tags: ["Industrial Edge Computing", "Industrial PCs", "AI Computing Infrastructure", "IPC-BOX-D6-M-0P", "CE Certified", "Fanless"],
  },
  {
    id: "dfoptix",
    name: "DFOPTIX",
    logo: "/neunexus/partners/logo-dfoptix.png",
    group: "precision-vision",
    status: "Proposed Collaboration",
    category: "Machine Vision · Metrology",
    country: "Germany",
    description: "Precision machine vision, optical inspection systems, sensor calibration and industrial metrology. Provides the quality-assurance layer for autonomous production lines.",
    contact: { name: "Technical Sales", email: "", phone: "", website: "" },
    documents: [],
    tags: ["Machine Vision", "Sensor Calibration", "Optical Inspection", "Industrial Metrology"],
  },
  {
    id: "bluesun",
    name: "Bluesun Solar",
    logo: "/neugreen/logo-bluesun.png",
    group: "renewable-infrastructure",
    status: "Proposed Collaboration",
    category: "OEM Solar · Private Label",
    country: "China",
    description: "Manufacturer of N-Type TOPCon high-efficiency PV modules, hybrid inverters and residential/commercial PV kits. OEM manufacturing and private-label solar modules for the NeuGreen generation layer.",
    contact: { name: "Sales Contact", email: "sales@bluesunpv.com", phone: "+86 21 xxxx xxxx", website: "https://www.bluesunpv.com" },
    documents: [
      { name: "Bluesun Solar Company Profile V8", type: "PDF", size: "10.3 MB", url: "https://customer-assets.emergentagent.com/job_hydro-protect-7/artifacts/kc09ecny_BLUESUN%20SOLAR%20COMPANY%20PROFILE%20202511128%20V8.%20%20%282%29%20%281%29.pdf" },
      { name: "BSM-5500BLV-48DA User Manual (v20251105)", type: "PDF", size: "User Manual", url: "https://customer-assets-cm19k8pv.emergentagent.net/job_hydro-protect-7/artifacts/qx1sbsic_BSM-5500BLV-48DA%20USER%20MANUAL%20V20251105.pdf" },
    ],
    tags: ["OEM Manufacturing", "Private Label Solar Modules", "N-Type TOPCon", "Hybrid Inverter", "Full Kits", "CE", "TÜV"],
  },
  {
    id: "wattzing",
    name: "Wattzing",
    logo: "/neugreen/logo-wattzing.png",
    group: "renewable-infrastructure",
    status: "Proposed Collaboration",
    category: "BESS · Hybrid Inverters",
    country: "China",
    description: "OEM manufacturer of Battery Energy Storage Systems (BESS), hybrid inverters and energy storage solutions from 5 kWh residential to 500 kWh commercial. Selected for NeuGreen residential and commercial storage.",
    contact: { name: "Sales Contact", email: "info@wattzing.com", phone: "+86 xxxxx", website: "https://www.wattzing.com" },
    documents: [
      { name: "Abby-Watting Product Catalog (v2)",  type: "PDF", size: "Catalog",       url: "https://customer-assets-cm19k8pv.emergentagent.net/job_hydro-protect-7/artifacts/fjw8wv3w_Abby-Watting%20-%20Product%20Catalog%20%281%29.pdf" },
      { name: "Energy Storage CE Certificate",      type: "PDF", size: "Certification", url: "https://customer-assets-cm19k8pv.emergentagent.net/job_hydro-protect-7/artifacts/9d8v9i3y_Energy%20storage%20CE%20%281%29.pdf" },
      { name: "New CE Certification",               type: "PDF", size: "Certification", url: "https://customer-assets-cm19k8pv.emergentagent.net/job_hydro-protect-7/artifacts/c342ol4t_NEW%20CE%20certification%20%281%29.pdf" },
      { name: "Najieer CE Certification",           type: "PDF", size: "53 KB",         url: "https://customer-assets.emergentagent.com/job_hydro-protect-7/artifacts/0zurigpd_Najieer_CE_certification2026-05-21_07_13_15.pdf" },
    ],
    tags: ["Battery Energy Storage Systems (BESS)", "Hybrid Inverters", "Energy Storage Solutions", "LFP", "5–500 kWh", "OEM"],
  },
  {
    id: "beny",
    name: "BENY New Energy",
    logo: "/neugreen/logo-beny.png",
    group: "renewable-infrastructure",
    status: "Proposed Collaboration",
    category: "EV Charging · DC Protection",
    country: "China",
    description: "Manufacturer of EV charging systems, DC protection devices, electrical components and smart energy infrastructure. Supplies NeuGreen's mobility layer and BESS containers.",
    contact: { name: "Sales Contact", email: "sales@beny.com", phone: "+86 xxxxx", website: "https://www.beny.com" },
    documents: [
      { name: "BENY ESS Datasheet — 100 kW / 241 kWh Air-Cooled BESS", type: "PDF", size: "Datasheet", url: "https://customer-assets-cm19k8pv.emergentagent.net/job_hydro-protect-7/artifacts/wdzhvj2c_BENY%20ESS%20Datasheet%28100kW-241kWh%20Air-Cooled%20Battery%20Storage%29%202025.3.21.pdf" },
      { name: "BENY Datasheet — Commercial & Industrial Energy Storage Battery", type: "PDF", size: "Datasheet", url: "https://customer-assets-cm19k8pv.emergentagent.net/job_hydro-protect-7/artifacts/qovywk17_BENY%20Datasheet%20of%20Commercial%20and%20Industrial%20Energy%20Storage%20Battery%20%281%29.pdf" },
      { name: "BYHV-241SAC CE EMC Certificate",       type: "PDF", size: "Certification", url: "https://customer-assets-cm19k8pv.emergentagent.net/job_hydro-protect-7/artifacts/egfq473z_BYHV-241SAC%20CE%20EMC%20Certificate%20.pdf" },
      { name: "CE-LVD Certificate No.2566AS04BUTO59140", type: "PDF", size: "Certification", url: "https://customer-assets-cm19k8pv.emergentagent.net/job_hydro-protect-7/artifacts/s5ryi9ny_No.2566AS04BUTO59140-CE-LVD%E8%AF%81%E4%B9%A6.pdf" },
      { name: "AC TÜV Certification",       type: "PDF", size: "916 KB", url: "https://customer-assets.emergentagent.com/job_hydro-protect-7/artifacts/sxfknm68_AC_TUV2026-05-21_07_13_15.pdf" },
      { name: "Energy Storage CE Certificate", type: "PDF", size: "53 KB",  url: "https://customer-assets.emergentagent.com/job_hydro-protect-7/artifacts/pfnt20ol_Energy%20storage%20CE%20%283%29.pdf" },
    ],
    tags: ["EV Charging Systems", "DC Protection", "Electrical Components", "Smart Energy Infrastructure", "241 kWh BESS", "CE-EMC", "CE-LVD", "TÜV"],
  },
  {
    id: "neura",
    name: "NEURA Robotics",
    logo: "/neunexus/partners/logo-neura.png",
    group: "autonomous-manufacturing",
    status: "Proposed Collaboration",
    category: "Cognitive Robotics · Humanoid",
    country: "Germany",
    description: "German pioneer in cognitive robotics and humanoid robots for autonomous manufacturing and human-robot collaboration. Proposed collaboration for the HX Smart Factory demonstrator.",
    contact: { name: "Partnerships", email: "", phone: "", website: "https://neura-robotics.com" },
    documents: [],
    tags: ["Cognitive Robotics", "Humanoid Robots", "Human-Robot Collaboration", "Autonomous Manufacturing"],
  },
  {
    id: "fraunhofer",
    name: "Fraunhofer",
    logo: "/neunexus/partners/logo-fraunhofer.png",
    group: "applied-research",
    status: "Proposed Research Collaboration",
    category: "Applied Research · Pilot Projects",
    country: "Germany",
    description: "Europe's largest applied research organisation. Proposed research collaboration for technology validation, pilot projects and industrial demonstrators within the NeuNexus and Smart Factory initiatives.",
    contact: { name: "Research Coordination", email: "", phone: "", website: "https://www.fraunhofer.de" },
    documents: [],
    tags: ["Applied Research", "Technology Validation", "Pilot Projects", "Industrial Demonstrators"],
  },
  {
    id: "renalix",
    name: "Renalix",
    logo: "/renalix/logo-official.png",
    group: "health-intelligence",
    status: "NeuNexus Division",
    category: "Predictive Kidney Intelligence · Healthcare AI",
    country: "Germany",
    description: "NeuNexus Health division — an AI healthcare platform delivering predictive kidney intelligence for clinicians and patients. Uetze Health Innovation Campus. Renalix Care (Pflege) and Renalix Pro (Hospitals) share a single European AI core. Launching 2027 (EU MDR track).",
    contact: { name: "Health Program", email: "", phone: "", website: "" },
    documents: [
      { title: "NeuNexus Health Campus · Strategic Vision", type: "PDF" },
    ],
    tags: ["Predictive Kidney Intelligence", "Digital Kidney Twin", "Renalix Care", "Renalix Pro", "GDPR · DSGVO", "FHIR", "EU MDR"],
  },
];

export const partnerBySlug = (slug) => SUPPLIERS.find((p) => p.id === slug);
export const groupById      = (id)   => STRATEGIC_GROUPS.find((g) => g.id === id);
